import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    projectType: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [focused, setFocused] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setFormData({
        name: "",
        email: "",
        phone: "",
        projectType: "",
        message: "",
      });
      setSubmitted(false);
    }, 5000);
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 bg-white bg-opacity-10 border rounded-lg text-white placeholder-gray-400 focus:outline-none transition-all duration-300 ${
      focused === field
        ? "border-cyan-400 shadow-lg shadow-cyan-400/20"
        : "border-gray-500 hover:border-gray-400"
    }`;

  const projectTypes = [
    "Kitchen Renovation",
    "Bathroom Remodel",
    "Deck / Patio",
    "Custom Carpentry",
    "Full Home Renovation",
    "Other",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white">
      {/* Hero */}
      <div className="pt-32 pb-16 text-center animate-fade-in px-4">
        <h1 className="text-6xl font-bold mb-4">
          Let's Build{" "}
          <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
            Together
          </span>
        </h1>
        <p className="text-xl text-gray-300 max-w-xl mx-auto">
          Have a project in mind? We'd love to hear about it. Reach out and
          let's make it happen.
        </p>
      </div>

      <div className="max-w-6xl mx-auto px-4 pb-20">
        <div className="grid md:grid-cols-5 gap-12">
          {/* ── Left: contact info ─────────────────────────────── */}
          <div className="md:col-span-2 space-y-8 animate-slide-in-left">
            {/* Info Cards */}
            {[
              {
                Icon: PhoneIcon,
                title: "Call Us",
                line1: "(555) 123-4567",
                line2: "Mon–Fri, 8 AM – 5 PM",
                href: "tel:+15551234567",
              },
              {
                Icon: EnvelopeIcon,
                title: "Email Us",
                line1: "info@ingersollbuilt.ca",
                line2: "We reply within 24 hours",
                href: "mailto:info@ingersollbuilt.ca",
              },
              {
                Icon: MapPinIcon,
                title: "Based In",
                line1: "Quispamsis, New Brunswick",
                line2: "Serving the surrounding region",
              },
            ].map((item) => {
              const Component = item.href ? "a" : "div";
              return (
                <Component
                  key={item.title}
                  href={item.href}
                  className="flex items-start gap-4 p-5 bg-white bg-opacity-5 border border-white border-opacity-10 rounded-xl hover:bg-opacity-10 transition-all duration-300 hover-card"
                  style={item.href ? { cursor: "pointer" } : {}}
                >
                  <item.Icon className="w-8 h-8 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-white mb-1">{item.title}</p>
                    <p
                      className={`${item.href ? "hover:text-cyan-300" : ""} text-gray-200 transition`}
                    >
                      {item.line1}
                    </p>
                    <p className="text-gray-400 text-sm">{item.line2}</p>
                  </div>
                </Component>
              );
            })}

            {/* Quick links */}
            <div className="pt-4">
              <p className="text-gray-400 text-sm mb-4">Explore our work</p>
              <div className="flex gap-3">
                <Link
                  to="/showcase"
                  className="px-5 py-2 bg-white bg-opacity-10 hover:bg-opacity-20 border border-white border-opacity-20 rounded-lg text-sm transition-all duration-300"
                >
                  View Showcase
                </Link>
                <Link
                  to="/"
                  className="px-5 py-2 bg-white bg-opacity-10 hover:bg-opacity-20 border border-white border-opacity-20 rounded-lg text-sm transition-all duration-300"
                >
                  Back to Home
                </Link>
              </div>
            </div>
          </div>

          {/* ── Right: form ────────────────────────────────────── */}
          <div className="md:col-span-3 animate-slide-in-right">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-12 bg-white bg-opacity-5 border border-white border-opacity-10 rounded-2xl animate-fade-in">
                <CheckCircleIcon className="w-16 h-16 text-green-400 mb-6 animate-bounce-gentle" />
                <h2 className="text-3xl font-bold mb-4">Message Sent!</h2>
                <p className="text-gray-300 text-lg">
                  Thanks for reaching out,{" "}
                  <strong>{formData.name || "there"}</strong>! We'll be in touch
                  within one business day.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white bg-opacity-5 border border-white border-opacity-10 rounded-2xl p-8 space-y-5 backdrop-blur-sm"
              >
                <h2 className="text-2xl font-bold mb-2">Send Us a Message</h2>

                {/* Name + Email row */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-300 text-sm font-semibold mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={() => setFocused("name")}
                      onBlur={() => setFocused("")}
                      placeholder="John Smith"
                      required
                      className={inputClass("name")}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm font-semibold mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => setFocused("email")}
                      onBlur={() => setFocused("")}
                      placeholder="john@example.com"
                      required
                      className={inputClass("email")}
                    />
                  </div>
                </div>

                {/* Phone + Project type row */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-300 text-sm font-semibold mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onFocus={() => setFocused("phone")}
                      onBlur={() => setFocused("")}
                      placeholder="(555) 123-4567"
                      className={inputClass("phone")}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm font-semibold mb-2">
                      Project Type
                    </label>
                    <select
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      onFocus={() => setFocused("projectType")}
                      onBlur={() => setFocused("")}
                      className={inputClass("projectType") + " cursor-pointer"}
                    >
                      <option value="" className="bg-gray-800">
                        Select a type…
                      </option>
                      {projectTypes.map((t) => (
                        <option key={t} value={t} className="bg-gray-800">
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-gray-300 text-sm font-semibold mb-2">
                    Tell Us About Your Project *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => setFocused("message")}
                    onBlur={() => setFocused("")}
                    required
                    rows="5"
                    placeholder="Describe your vision, timeline, budget range…"
                    className={inputClass("message")}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold text-lg rounded-lg hover:shadow-2xl hover:shadow-cyan-500/30 transform hover:scale-105 transition-all duration-300"
                >
                  Send Message →
                </button>

                <p className="text-center text-gray-500 text-xs">
                  We respect your privacy and will never share your information.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;
